import * as React from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { EMAIL_CONSTRAINT as getEmailFormatError } from 'bundles/authentication-common/constants/constraints';
import { useEnterpriseProgramsByOrganizationSlug } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseProgramsByOrganizationSlug';
import { useSystemProgram } from 'bundles/enterprise-admin-hooks/hooks/useSystemProgram';
import ProgramInvitationsDomainAllowListMultiDeleteMutationGql from 'bundles/enterprise-admin-program-creation/queries/ProgramInvitationsDomainAllowListMultiDeleteMutation.graphql';
import ProgramInvitationsDomainAllowListMultiUpdateMutationGql from 'bundles/enterprise-admin-program-creation/queries/ProgramInvitationsDomainAllowListMultiUpdateMutation.graphql';
import type {
  MultiDeleteMutation,
  MultiDeleteMutationVariables,
} from 'bundles/enterprise-admin-program-creation/queries/__generated__/ProgramInvitationsDomainAllowListMultiDeleteMutation';
import type {
  MultiUpdateMutation,
  MultiUpdateMutationVariables,
} from 'bundles/enterprise-admin-program-creation/queries/__generated__/ProgramInvitationsDomainAllowListMultiUpdateMutation';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

import _t from 'i18n!nls/enterprise-admin-learners';

import DomainAllowlistByOrganizationGql from '../queries/DomainAllowlistByOrganization.graphql';
import DomainAllowlistRostersGql from '../queries/DomainAllowlistRosters.graphql';
import type {
  DomainAllowlistByOrganizationQuery,
  DomainAllowlistByOrganizationQueryVariables,
} from '../queries/__generated__/DomainAllowlistByOrganization';
import type {
  DomainAllowlistRostersQuery,
  DomainAllowlistRostersQueryVariables,
} from '../queries/__generated__/DomainAllowlistRosters';

/** One row of the org-wide merged allowlist table: a single (domain, group) pair. The same
 * domain allowlisted under two different groups produces two separate rows. */
export type AllowlistRow = {
  emailDomain: string;
  programId: string;
  groupLabel: string;
};

/** SelectField sentinel for "No group – Add to All learners" (targets the org's System Program). */
const NO_GROUP_VALUE = '';

type UseDomainAllowlistArgs = {
  /** True while the modal is open — resets all local state when it closes. */
  open: boolean;
  /** Set when the group step should be skipped (Group Details, or a Group admin with exactly
   * one group) — pre-selects and locks the group picker, mirroring EmailInviteModal/CsvInviteModal. */
  autoSelectedGroupId?: string;
};

/**
 * Group-scoped domain allowlisting (ENTADMIN-1956). A "group" is just an `EnterpriseProgram`;
 * "No group" targets the org's System Program.
 */
export const useDomainAllowlist = ({ open, autoSelectedGroupId }: UseDomainAllowlistArgs) => {
  const org = useThirdPartyOrganization();
  const { programs, loading: programsLoading } = useEnterpriseProgramsByOrganizationSlug(org.slug);
  const { systemProgram, loading: systemProgramLoading } = useSystemProgram(org.id);
  // The system program is already offered as the "No group – Add to All learners" sentinel
  // above, so exclude it here to avoid it also appearing as a raw, unlabeled group option.
  const groupOptions = React.useMemo(
    () => (programs ?? []).filter((program) => program.id !== systemProgram?.id),
    [programs, systemProgram]
  );

  const [selectedGroupId, setSelectedGroupId] = React.useState(autoSelectedGroupId ?? NO_GROUP_VALUE);
  const [domainInputText, setDomainInputText] = React.useState('');
  const [domainInputError, setDomainInputError] = React.useState<string | undefined>(undefined);
  const [mutationError, setMutationError] = React.useState<string | undefined>(undefined);
  const [pendingDeleteRow, setPendingDeleteRow] = React.useState<AllowlistRow | undefined>(undefined);

  React.useEffect(() => {
    if (!open) {
      setDomainInputText('');
      setDomainInputError(undefined);
      setMutationError(undefined);
      setPendingDeleteRow(undefined);
      return;
    }
    setSelectedGroupId(autoSelectedGroupId ?? NO_GROUP_VALUE);
  }, [open, autoSelectedGroupId]);

  const isGroupSelectionLocked = Boolean(autoSelectedGroupId);
  const showGroupPicker = groupOptions.length > 0 || isGroupSelectionLocked;
  const selectedGroup = groupOptions.find((group) => group.id === selectedGroupId);
  const targetProgramId = selectedGroup ? selectedGroup.id : systemProgram?.id;
  const targetProgramSlug = selectedGroup ? selectedGroup.slug : systemProgram?.slug;
  const targetGroupLabel = selectedGroup ? selectedGroup.name : _t('All learners');

  const {
    data: rosterData,
    loading: rosterLoading,
    error: rosterError,
  } = useQuery<DomainAllowlistRostersQuery, DomainAllowlistRostersQueryVariables>(DomainAllowlistRostersGql, {
    variables: { programIds: targetProgramId ? [targetProgramId] : [] },
    skip: !targetProgramId,
    context: { clientName: 'gatewayGql' },
  });
  const rosterId =
    rosterData?.EnterpriseRosters?.queryRostersByProgramIdsOrContractIdsOrBothAndIncludeDeleted?.elements[0]?.id;
  const hasRosterError = !rosterLoading && Boolean(rosterError);
  const hasNoRoster = !rosterLoading && !hasRosterError && Boolean(targetProgramId) && !rosterId;

  const {
    data: allowlistData,
    loading: allowlistLoading,
    refetch: refetchAllowlist,
  } = useQuery<DomainAllowlistByOrganizationQuery, DomainAllowlistByOrganizationQueryVariables>(
    DomainAllowlistByOrganizationGql,
    {
      variables: { organizationId: org.id ?? '' },
      skip: !org.id,
      context: { clientName: 'gatewayGql' },
    }
  );

  const groupLabelByProgramId = React.useMemo(() => {
    const labels = new Map<string, string>();
    groupOptions.forEach((group) => labels.set(group.id, group.name));
    if (systemProgram) labels.set(systemProgram.id, _t('All learners'));
    return labels;
  }, [groupOptions, systemProgram]);

  // One row per (domain, group) pair — the API groups by program, so flatten it. The same
  // domain allowlisted under two different groups intentionally produces two separate rows.
  const allowlist: AllowlistRow[] = React.useMemo(
    () =>
      (allowlistData?.ProgramInvitationsDomainAllowList?.queryDomainAllowListByOrganization ?? []).flatMap(
        ({ programId, emailDomains }) =>
          emailDomains.map((emailDomain) => ({
            emailDomain,
            programId,
            groupLabel: groupLabelByProgramId.get(programId) ?? _t('Another group'),
          }))
      ),
    [allowlistData, groupLabelByProgramId]
  );
  const targetProgramAllowlist = React.useMemo(
    () => allowlist.filter((entry) => entry.programId === targetProgramId),
    [allowlist, targetProgramId]
  );

  const [multiUpdateDomains, { loading: isAddingDomains }] = useMutation<
    MultiUpdateMutation,
    MultiUpdateMutationVariables
  >(ProgramInvitationsDomainAllowListMultiUpdateMutationGql, { context: { clientName: 'gatewayGql' } });
  const [multiDeleteDomains, { loading: isRemovingDomain }] = useMutation<
    MultiDeleteMutation,
    MultiDeleteMutationVariables
  >(ProgramInvitationsDomainAllowListMultiDeleteMutationGql, { context: { clientName: 'gatewayGql' } });

  const hasInvalidDomains = (domains: string[]) =>
    domains.some((domain) => Boolean(getEmailFormatError(`prefix@${domain}`)));

  const hasDuplicateDomains = (domains: string[]) =>
    domains.some(
      (domain, index) =>
        targetProgramAllowlist.some((entry) => entry.emailDomain === domain) || domains.indexOf(domain) !== index
    );

  const onSelectGroup = (groupId: string) => {
    setSelectedGroupId(groupId);
    setDomainInputError(undefined);
    setMutationError(undefined);
  };

  const onDomainInputTextChange = (text: string) => {
    setDomainInputText(text);
    setDomainInputError(undefined);
  };

  const addDomains = async () => {
    setDomainInputError(undefined);
    setMutationError(undefined);

    const domains = domainInputText
      .split(',')
      .map((domain) => domain.trim().toLowerCase())
      .filter(Boolean);
    if (domains.length === 0) return;

    if (hasInvalidDomains(domains)) {
      setDomainInputError(_t('Enter a valid domain name, like example.com.'));
      return;
    }
    if (hasDuplicateDomains(domains)) {
      setDomainInputError(_t('This domain has already been added.'));
      return;
    }
    if (!targetProgramId || !rosterId || hasRosterError) return;

    try {
      const response = await multiUpdateDomains({
        variables: { input: { programId: targetProgramId, emailDomains: domains, rosterId, insertOnly: false } },
      });
      if (response.errors) {
        setMutationError(_t('Something went wrong while adding your domain(s). Please try again.'));
        return;
      }
      await refetchAllowlist();
      setDomainInputText('');
    } catch {
      setMutationError(_t('Something went wrong while adding your domain(s). Please try again.'));
    }
  };

  const requestRemoveDomain = React.useCallback<(row: AllowlistRow) => void>((row) => setPendingDeleteRow(row), []);
  const cancelRemoveDomain = () => setPendingDeleteRow(undefined);

  const confirmRemoveDomain = async () => {
    if (!pendingDeleteRow || isRemovingDomain) return;
    setMutationError(undefined);
    try {
      const response = await multiDeleteDomains({
        variables: {
          input: { programId: pendingDeleteRow.programId, emailDomains: [pendingDeleteRow.emailDomain] },
        },
      });
      if (response.errors) {
        setMutationError(_t('Something went wrong while removing this domain. Please try again.'));
        return;
      }
      await refetchAllowlist();
    } catch {
      setMutationError(_t('Something went wrong while removing this domain. Please try again.'));
    } finally {
      setPendingDeleteRow(undefined);
    }
  };

  return {
    showGroupPicker,
    isGroupSelectionLocked,
    groupOptions,
    selectedGroupId,
    onSelectGroup,
    targetGroupLabel,
    targetProgramSlug,
    allowlist,
    targetProgramAllowlist,
    canSave: targetProgramAllowlist.length > 0 && !isAddingDomains,
    loading: programsLoading || systemProgramLoading || allowlistLoading,
    hasNoRoster,
    hasRosterError,
    domainInputText,
    onDomainInputTextChange,
    domainInputError,
    mutationError,
    addDomains,
    isAddingDomains,
    pendingDeleteRow,
    requestRemoveDomain,
    cancelRemoveDomain,
    confirmRemoveDomain,
    isRemovingDomain,
  };
};
